import { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TrendingUp, TrendingDown, PackageOpen, BadgePercent, Flame, CalendarRange, Loader2, Wand2 } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const ALL = "__all__";

const STRATEGIES = [
  { key: "top_sellers", label: "Top sellers", hint: "Your best-selling products: draw customers in", icon: TrendingUp, days: 90 },
  { key: "slow_movers", label: "Slow movers", hint: "Stock that barely sells: clear it out", icon: TrendingDown, days: 90 },
  { key: "overstocked", label: "Overstocked", hint: "Above max level or too many months of stock", icon: PackageOpen, days: 90 },
  { key: "best_margin", label: "Best margin", hint: "High GP%: can afford a discount", icon: BadgePercent, days: 90 },
  { key: "trending", label: "Trending", hint: "Selling more than the period before", icon: Flame, days: 30 },
  { key: "seasonal", label: "Seasonal", hint: "Sold well in the coming weeks last year", icon: CalendarRange, days: 30 },
];
const PERIODS = [30, 60, 90, 180, 365];
const LIMITS = [10, 20, 50, 100];
const fmtR = (v) => `R${(parseFloat(v) || 0).toFixed(2)}`;

const Picker = ({ label, value, onChange, options, testId }) => (
  <div>
    <Label className="text-xs">{label}</Label>
    <Select value={String(value)} onValueChange={onChange}>
      <SelectTrigger className="h-8 text-xs" data-testid={testId}><SelectValue /></SelectTrigger>
      <SelectContent>{options.map((o) => <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>)}</SelectContent>
    </Select>
  </div>
);

export const PromoPickerDialog = ({ open, onClose, promotions, onCreate, onAddToExisting }) => {
  const [strategy, setStrategy] = useState("top_sellers");
  const [days, setDays] = useState(90);
  const [limit, setLimit] = useState(20);
  const [rankBy, setRankBy] = useState("units");
  const [department, setDepartment] = useState(ALL);
  const [category, setCategory] = useState(ALL);
  const [inStock, setInStock] = useState(true);
  const [excludePromo, setExcludePromo] = useState(true);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [picked, setPicked] = useState({});
  const [targetPromo, setTargetPromo] = useState("");

  const run = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/promotions/suggest-products`, { params: {
        strategy, days, limit, rank_by: rankBy, in_stock_only: inStock, exclude_promo_and_no_discount: excludePromo,
        department: department === ALL ? undefined : department, category: category === ALL ? undefined : category,
      } });
      setData(res.data);
      setPicked({});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load suggestions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (open) run(); }, [open, strategy, days, limit, rankBy, department, category, inStock, excludePromo]); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = data?.products || [];
  const chosen = rows.filter((r) => picked[r.id]);
  const customPromos = (promotions || []).filter((p) => p.promotion_type === "custom_price");
  const allPicked = rows.length > 0 && chosen.length === rows.length;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto" data-testid="promo-picker-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Wand2 className="h-5 w-5 text-purple-600" />Pick Products for Promo</DialogTitle></DialogHeader>

        <div className="grid grid-cols-2 md:grid-cols-6 gap-2" data-testid="promo-picker-strategies">
          {STRATEGIES.map((s) => (
            <button key={s.key} type="button" onClick={() => { setStrategy(s.key); setDays(s.days); }} data-testid={`promo-strategy-${s.key}`}
              className={`rounded-lg border p-2 text-left transition-colors ${strategy === s.key ? "border-purple-600 bg-purple-50" : "hover:bg-gray-50"}`}>
              <div className="flex items-center gap-1.5 text-sm font-semibold"><s.icon className="h-4 w-4 text-purple-600" />{s.label}</div>
              <div className="mt-0.5 text-[11px] leading-tight text-gray-500">{s.hint}</div>
            </button>
          ))}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-6 gap-2 items-end">
          <Picker label="Period" value={days} onChange={(v) => setDays(Number(v))} options={PERIODS.filter((d) => strategy !== "seasonal" || d <= 90).map((d) => ({ value: d, label: strategy === "seasonal" ? `Next ${d} days (last year)` : `Last ${d} days` }))} testId="promo-picker-period" />
          <Picker label="Show" value={limit} onChange={(v) => setLimit(Number(v))} options={LIMITS.map((l) => ({ value: l, label: `Top ${l}` }))} testId="promo-picker-limit" />
          {strategy === "top_sellers" && <Picker label="Rank by" value={rankBy} onChange={setRankBy} options={[{ value: "units", label: "Units sold" }, { value: "value", label: "Rand value" }]} testId="promo-picker-rank" />}
          <Picker label="Department" value={department} onChange={setDepartment} options={[{ value: ALL, label: "All departments" }, ...(data?.facets?.departments || []).map((d) => ({ value: d, label: d }))]} testId="promo-picker-department" />
          <Picker label="Category" value={category} onChange={setCategory} options={[{ value: ALL, label: "All categories" }, ...(data?.facets?.categories || []).map((c) => ({ value: c, label: c }))]} testId="promo-picker-category" />
          <div className="space-y-1 text-xs">
            <label className="flex items-center gap-1.5"><Checkbox checked={inStock} onCheckedChange={(v) => setInStock(!!v)} data-testid="promo-picker-in-stock" />In stock only</label>
            <label className="flex items-center gap-1.5"><Checkbox checked={excludePromo} onCheckedChange={(v) => setExcludePromo(!!v)} data-testid="promo-picker-exclude" />Skip on-promo / No Discount</label>
          </div>
        </div>

        <div className="rounded-md border">
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-10 text-sm text-gray-500"><Loader2 className="h-4 w-4 animate-spin" />Finding products…</div>
          ) : rows.length === 0 ? (
            <div className="py-10 text-center text-sm text-gray-500" data-testid="promo-picker-empty">No products match. Try another strategy, a longer period or fewer filters.</div>
          ) : (
            <div className="max-h-[45vh] overflow-auto">
              <table className="w-full text-sm" data-testid="promo-picker-results">
                <thead className="sticky top-0 bg-gray-100 text-xs">
                  <tr className="text-left">
                    <th className="p-2"><Checkbox checked={allPicked} onCheckedChange={(v) => setPicked(v ? Object.fromEntries(rows.map((r) => [r.id, true])) : {})} data-testid="promo-picker-select-all" /></th>
                    <th className="p-2">Product</th><th className="p-2">Dept / Category</th><th className="p-2 text-right">Stock</th>
                    <th className="p-2 text-right">Sold</th><th className="p-2 text-right">Price</th><th className="p-2 text-right">GP%</th><th className="p-2">Why</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className={`border-t ${picked[r.id] ? "bg-purple-50" : "hover:bg-gray-50"}`} data-testid={`promo-picker-row-${r.plu_code}`}>
                      <td className="p-2"><Checkbox checked={!!picked[r.id]} onCheckedChange={(v) => setPicked((p) => ({ ...p, [r.id]: !!v }))} data-testid={`promo-picker-check-${r.plu_code}`} /></td>
                      <td className="p-2"><div className="font-medium">{r.name}</div><div className="text-xs text-gray-500">{r.plu_code}</div></td>
                      <td className="p-2 text-xs text-gray-600">{r.department || "-"} / {r.category || "-"}</td>
                      <td className="p-2 text-right font-mono">{r.current_stock}</td>
                      <td className="p-2 text-right font-mono">{r.units_sold}</td>
                      <td className="p-2 text-right font-mono">{fmtR(r.selling_price)}</td>
                      <td className="p-2 text-right font-mono">{r.gp_percent ?? "-"}</td>
                      <td className="p-2 text-xs text-gray-600">{r.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        {data && <div className="text-xs text-gray-500">{data.count} products matched, showing {rows.length}. {chosen.length} selected.</div>}

        <DialogFooter className="flex-wrap gap-2 sm:justify-between">
          <div className="flex items-center gap-2">
            <Select value={targetPromo} onValueChange={setTargetPromo}>
              <SelectTrigger className="h-9 w-64 text-xs" data-testid="promo-picker-existing-select"><SelectValue placeholder="Existing custom-price promotion…" /></SelectTrigger>
              <SelectContent>
                {customPromos.length === 0 && <div className="p-2 text-xs text-gray-500">No custom-price promotions yet</div>}
                {customPromos.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant="outline" disabled={!chosen.length || !targetPromo} onClick={() => onAddToExisting(customPromos.find((p) => p.id === targetPromo), chosen)} data-testid="promo-picker-add-existing-btn">Add to Promotion</Button>
          </div>
          <Button disabled={!chosen.length} onClick={() => onCreate(chosen)} data-testid="promo-picker-create-btn">Create Promotion with {chosen.length || ""} selected</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
